import type { SocialMedia } from './types';

export type MediaUploadProgress = { percent: number; phase: 'uploading' | 'processing' };

export function uploadSocialMedia(file: File, onProgress?: (progress: MediaUploadProgress) => void, signal?: AbortSignal): Promise<{ media: SocialMedia }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let offset = 0, buffered = '', media: SocialMedia | undefined, failure: string | undefined;
    let settled = false, uploadComplete = false;
    let deadline: ReturnType<typeof setTimeout>, idle: ReturnType<typeof setTimeout>;
    const cleanup = () => { clearTimeout(deadline); clearTimeout(idle); signal?.removeEventListener('abort', abort); };
    const fail = (error: Error) => { if (!settled) { settled = true; cleanup(); reject(error); } };
    const abort = () => { xhr.abort(); fail(new DOMException('Upload cancelled.', 'AbortError')); };
    const timeout = (message: string) => { fail(new Error(message)); xhr.abort(); };
    const activity = () => {
      if (settled) return;
      clearTimeout(idle);
      idle = setTimeout(() => timeout(uploadComplete ? 'The processing connection stopped responding. Please retry.' : 'The upload stopped transferring. Check your connection and retry.'), 90000);
    };
    const readRecords = (final = false) => {
      buffered += xhr.responseText.slice(offset); offset = xhr.responseText.length;
      const records = buffered.split('\n'); buffered = records.pop() || '';
      if (final && buffered.trim()) { records.push(buffered); buffered = ''; }
      for (const record of records) {
        if (!record.trim()) continue;
        try {
          const data = JSON.parse(record);
          if (data.status === 'processing' && uploadComplete) onProgress?.({ percent: 100, phase: 'processing' });
          if (typeof data.error === 'string') failure = data.error;
          if (data.media?.id && data.media?.url) media = data.media;
        } catch { failure = 'The upload response was interrupted. Please retry.'; }
      }
    };
    xhr.open('POST', '/api/social/media');
    xhr.setRequestHeader('Accept', 'application/x-ndjson');
    // An XHR timeout includes bytes-in-flight. A slow upload must not consume
    // the encoder's budget. Heartbeats reset only the idle timer, never deadlines.
    xhr.timeout = 0;
    xhr.upload.onprogress = event => {
      activity();
      if (event.lengthComputable) onProgress?.({ percent: Math.min(100, Math.floor(event.loaded / event.total * 100)), phase: 'uploading' });
    };
    xhr.upload.onload = () => {
      if (settled) return;
      uploadComplete = true; clearTimeout(deadline); activity();
      deadline = setTimeout(() => timeout('Your file uploaded, but the server could not finish processing it in time. Please retry in a moment.'), 360000);
      onProgress?.({ percent: 100, phase: 'processing' });
    };
    xhr.onprogress = () => { activity(); if (xhr.getResponseHeader('content-type')?.includes('application/x-ndjson')) readRecords(); };
    xhr.onload = () => {
      const streaming = xhr.getResponseHeader('content-type')?.includes('application/x-ndjson');
      if (streaming) readRecords(true);
      else {
        try { const data = JSON.parse(xhr.responseText); failure = data.error; media = data.media; } catch { /* Use a useful HTTP error below. */ }
      }
      if (xhr.status < 200 || xhr.status >= 300 || failure || !media?.id || !media.url) {
        fail(new Error(failure || (xhr.status === 401 ? 'Please log in to upload media.' : xhr.status === 413 ? 'This file is too large. Videos support up to 30 MB and photos up to 10 MB.' : [502, 503, 504].includes(xhr.status) ? 'The upload connection was interrupted while processing. Please retry.' : 'The upload could not be completed. Please retry.')));
      } else if (!settled) { settled = true; cleanup(); resolve({ media }); }
    };
    xhr.onerror = () => fail(new Error('The upload connection was lost. Check your connection and retry.'));
    xhr.ontimeout = () => fail(new Error(uploadComplete ? 'The server could not finish processing. Please retry in a moment.' : 'The upload timed out. Check your connection and retry.'));
    xhr.onabort = () => fail(new DOMException('Upload cancelled.', 'AbortError'));
    if (signal?.aborted) { abort(); return; }
    signal?.addEventListener('abort', abort, { once: true });
    deadline = setTimeout(() => timeout('The upload took too long. Check your connection or choose a smaller file.'), 600000);
    activity();
    onProgress?.({ percent: 0, phase: 'uploading' });
    const form = new FormData(); form.set('file', file);
    xhr.send(form);
  });
}
